import React, { useState, useRef, useEffect, useCallback } from 'react';
import { clampIndex, resolveIndex } from '../../lib/carousel';

// Swipe threshold as a fraction of the frame width (plan Step 5: swipe on
// touch, drag on desktop).
const SWIPE_THRESHOLD_FRACTION = 0.2;

const CarouselSimulator = ({ blobs }) => {
  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0); // live drag offset (px)
  const [dragging, setDragging] = useState(false);
  const [stitchMode, setStitchMode] = useState(false);
  const [urls, setUrls] = useState([]);

  const frameRef = useRef(null);
  const dragRef = useRef(null); // { startX, startIndex, threshold }

  const n = blobs.length;

  // Object URLs for the slice blobs; revoked on change/unmount.
  useEffect(() => {
    if (!blobs.length) return;
    const created = blobs.map((b) => URL.createObjectURL(b));
    setUrls(created);
    setIndex(0);

    return () => created.forEach((u) => URL.revokeObjectURL(u));
  }, [blobs]);

  const onPointerDown = useCallback(
    (e) => {
      if (stitchMode || !frameRef.current) return;
      const rect = frameRef.current.getBoundingClientRect();
      dragRef.current = {
        startX: e.clientX,
        startIndex: index,
        threshold: rect.width * SWIPE_THRESHOLD_FRACTION,
      };
      setDragging(true);

      try {
        frameRef.current.setPointerCapture?.(e.pointerId);
      } catch {
        // jsdom and some browsers throw for inactive pointerIds; drag
        // tracking works without capture (pointerleave ends it).
      }
    },
    [index, stitchMode]
  );

  const onPointerMove = useCallback(
    (e) => {
      const drag = dragRef.current;

      if (!drag || !dragging) return;
      setDragX(e.clientX - drag.startX);
    },
    [dragging]
  );

  const onPointerUp = useCallback(() => {
    const drag = dragRef.current;

    if (!drag) return;
    const next = resolveIndex(drag.startIndex, dragX, drag.threshold, n);
    setIndex(next);
    setDragX(0);
    setDragging(false);
    dragRef.current = null;
  }, [dragX, n]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowRight') {
        setIndex((i) => clampIndex(i + 1, n));
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        setIndex((i) => clampIndex(i - 1, n));
        e.preventDefault();
      }
    },
    [n]
  );

  if (n === 0) return null;

  // Track position: settled index + live drag, clamped by the frame.
  const trackX = -(index * 100) + (dragging ? (dragX / (frameRef.current?.clientWidth || 1)) * 100 : 0);

  return (
    <div className="carousel">
      <div className="carousel-toolbar">
        <div role="group" aria-label="Carousel controls">
          <button type="button" onClick={() => setStitchMode(false)} className={!stitchMode ? 'active' : ''}>
            Carousel
          </button>
          <button type="button" onClick={() => setStitchMode(true)} className={stitchMode ? 'active' : ''}>
            Stitch check
          </button>
        </div>
        <p className="carousel-hint">
          {stitchMode
            ? 'All slices edge-to-edge — confirm continuity before publishing.'
            : 'Swipe or drag to preview the Bluesky carousel.'}
        </p>
      </div>

      {stitchMode ? (
        <div className="stitch-strip" data-testid="stitch-strip">
          {urls.map((u, i) => (
            <img key={i} src={u} alt={`Slice ${i + 1} of ${n}`} />
          ))}
        </div>
      ) : (
        <div
          className="carousel-frame"
          ref={frameRef}
          tabIndex={0}
          role="group"
          aria-label={`Carousel preview: slide ${index + 1} of ${n}. Use arrow keys or drag to navigate.`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
          onKeyDown={onKeyDown}
        >
          <span className="carousel-badge" aria-live="polite">
            {index + 1}/{n}
          </span>
          <div
            className="carousel-track"
            data-testid="carousel-track"
            style={{
              transform: `translateX(${trackX}%)`,
              transition: dragging ? 'none' : 'transform 200ms ease',
            }}
          >
            {urls.map((u, i) => (
              <img key={i} src={u} alt={`Slice ${i + 1} of ${n}`} draggable={false} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarouselSimulator;
